import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Field, Input, Select, Fold } from "../ui";
import { SettingsSection, StatusText } from "./SettingsSection";
import { useSession } from "./session";

interface ImporterInfo {
  id: string;
  label: string;
  accept: string;
  level: string;
  online: "jira-api" | "ms-signin" | null;
  onlineReady: boolean;
}

interface Preview {
  tasks: number;
  new: number;
  alreadyImported: number;
  subtasks: number;
  groups: number;
  tags: number;
  recurring: number;
  done: number;
  timelogEntries: number;
  counters: number;
  warnings: string[];
}

interface Signin {
  flow: string;
  userCode: string;
  verificationUri: string;
  interval: number;
}

/**
 * Settings → Import from another app (I4): Super Productivity, Todoist, TickTick, Trello, Jira, Microsoft
 * To Do. Choose the app, give Drang its export (or sign in / fetch), look at the preview, then import.
 * Importing again later only adds what is new.
 */
export function ImportSettings() {
  const { t } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const [importers, setImporters] = useState<ImporterInfo[]>([]);
  const [source, setSource] = useState("");
  const [preview, setPreview] = useState<{ token: string; preview: Preview } | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [jiraForm, setJiraForm] = useState({ baseUrl: "", email: "", token: "", jql: "" });
  const [signin, setSignin] = useState<Signin | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const q = workspaceId ? `?workspace=${encodeURIComponent(workspaceId)}` : "";

  useEffect(() => {
    if (!workspaceId) return;
    void fetch(`/api/internal/import?workspace=${encodeURIComponent(workspaceId)}`, {
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? (r.json() as Promise<{ importers: ImporterInfo[] }>) : null))
      .then((d) => d && setImporters(d.importers));
  }, [workspaceId]);

  const failed = (code?: string) =>
    setMessage({ ok: false, text: t(`errors.${code}`, { defaultValue: t("import.failed") }) });

  async function send(path: string, init: RequestInit) {
    setBusy(true);
    setMessage(null);
    setPreview(null);
    try {
      const r = await fetch(path + q, { method: "POST", credentials: "same-origin", ...init });
      const body = (await r.json()) as {
        error?: string;
        token?: string;
        preview?: Preview;
        pending?: boolean;
      };
      if (!r.ok) failed(body.error);
      else if (body.token && body.preview) setPreview({ token: body.token, preview: body.preview });
      return body;
    } catch {
      failed();
      return null;
    } finally {
      setBusy(false);
    }
  }

  const upload = (file: File) => {
    const form = new FormData();
    form.append("file", file);
    void send(`/api/internal/import/${source}/preview`, { body: form });
  };

  const fetchJira = () =>
    void send("/api/internal/import/jira/fetch", {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(jiraForm),
    });

  const startSignin = async () => {
    const body = (await send("/api/internal/import/mstodo/signin", {})) as Signin | null;
    if (body?.flow) setSignin(body);
  };

  // while the person signs in at Microsoft, ask every few seconds whether it is done
  useEffect(() => {
    if (!signin) return;
    const id = setInterval(
      () => {
        void fetch(`/api/internal/import/mstodo/poll${q}`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ flow: signin.flow }),
        })
          .then(async (r) => ({
            ok: r.ok,
            body: (await r.json()) as {
              error?: string;
              token?: string;
              preview?: Preview;
              pending?: boolean;
            },
          }))
          .then(({ ok, body }) => {
            if (ok && body.pending) return;
            setSignin(null);
            if (ok && body.token && body.preview) setPreview({ token: body.token, preview: body.preview });
            else
              setMessage({
                ok: false,
                text: t(`errors.${body.error}`, { defaultValue: t("import.failed") }),
              });
          })
          .catch(() => undefined);
      },
      Math.max(3, signin.interval) * 1000,
    );
    return () => clearInterval(id);
  }, [signin, q, t]);

  const run = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/internal/import/run${q}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: preview.token }),
      });
      const body = (await r.json()) as { error?: string; report?: Record<string, number> };
      if (r.ok && body.report) setMessage({ ok: true, text: t("import.done", { ...body.report }) });
      else failed(body.error);
    } finally {
      setPreview(null);
      setBusy(false);
    }
  };

  const chosen = importers.find((i) => i.id === source);
  const p = preview?.preview;
  if (!workspaceId) return null;
  return (
    <SettingsSection
      id="settings-import"
      intro={t("import.hint")}
      summary={t("settings.summary.import")}
      title={t("import.title")}
      testId="settings-import"
      folded
    >
      <Field label={t("import.from")} htmlFor="import-source">
        <Select
          id="import-source"
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setPreview(null);
            setMessage(null);
            setSignin(null);
          }}
          data-testid="import-source"
        >
          <option value="">{t("import.choose")}</option>
          {importers.map((i) => (
            <option key={i.id} value={i.id}>
              {i.label}
            </option>
          ))}
        </Select>
      </Field>

      {chosen && (
        <div className="flex flex-col gap-3" data-testid="import-how">
          <p className="text-[13px] leading-relaxed">{t(`import.how.${chosen.id}`)}</p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={chosen.accept}
              className="sr-only"
              aria-label={t("import.file")}
              data-testid="import-source-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload(f);
                e.target.value = "";
              }}
            />
            <Button variant="secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
              {t("import.file")}
            </Button>
            <span className="text-[12px] text-neutral-500">{chosen.accept}</span>
          </div>

          {chosen.online === "jira-api" && (
            <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="import-jira">
              <legend className="mb-1 text-[12.5px] text-neutral-500">{t("import.jira.title")}</legend>
              <Field label={t("import.jira.baseUrl")} htmlFor="jira-url">
                <Input
                  id="jira-url"
                  placeholder="https://example.atlassian.net"
                  value={jiraForm.baseUrl}
                  onChange={(e) => setJiraForm({ ...jiraForm, baseUrl: e.target.value })}
                />
              </Field>
              <Field label={t("import.jira.email")} hint={t("import.jira.emailHint")} htmlFor="jira-email">
                <Input
                  id="jira-email"
                  value={jiraForm.email}
                  onChange={(e) => setJiraForm({ ...jiraForm, email: e.target.value })}
                />
              </Field>
              <Field label={t("import.jira.token")} hint={t("import.jira.tokenHint")} htmlFor="jira-token">
                <Input
                  id="jira-token"
                  type="password"
                  autoComplete="off"
                  value={jiraForm.token}
                  onChange={(e) => setJiraForm({ ...jiraForm, token: e.target.value })}
                />
              </Field>
              <Field label={t("import.jira.jql")} hint={t("import.jira.jqlHint")} htmlFor="jira-jql">
                <Input
                  id="jira-jql"
                  placeholder="project = SHOP"
                  value={jiraForm.jql}
                  onChange={(e) => setJiraForm({ ...jiraForm, jql: e.target.value })}
                />
              </Field>
              <div>
                <Button
                  variant="secondary"
                  disabled={busy || !jiraForm.baseUrl.trim() || !jiraForm.token.trim()}
                  onClick={fetchJira}
                >
                  {t("import.jira.fetch")}
                </Button>
              </div>
            </fieldset>
          )}

          {chosen.online === "ms-signin" &&
            (chosen.onlineReady ? (
              <div className="flex flex-col gap-2" data-testid="import-ms">
                {!signin ? (
                  <div>
                    <Button variant="secondary" disabled={busy} onClick={() => void startSignin()}>
                      {t("import.ms.signin")}
                    </Button>
                  </div>
                ) : (
                  <p className="text-[13px] leading-relaxed" data-testid="import-ms-code">
                    {t("import.ms.step", { url: signin.verificationUri })}{" "}
                    <span className="font-mono text-[15px] font-semibold">{signin.userCode}</span>{" "}
                    <a
                      href={signin.verificationUri}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent underline"
                    >
                      {t("import.ms.open")}
                    </a>
                  </p>
                )}
              </div>
            ) : (
              <p className="text-[12.5px] text-neutral-500">{t("import.ms.notReady")}</p>
            ))}
        </div>
      )}

      {p && (
        <div
          className="flex flex-col gap-2 rounded-md border border-neutral-200 p-3 dark:border-neutral-800"
          data-testid="import-preview"
        >
          <p className="text-sm">
            {t("import.preview", {
              tasks: p.tasks,
              subtasks: p.subtasks,
              groups: p.groups,
              tags: p.tags,
              recurring: p.recurring,
              done: p.done,
            })}
          </p>
          {p.timelogEntries + p.counters > 0 && (
            <p className="text-[12.5px] text-neutral-500">
              {t("import.previewTime", { entries: p.timelogEntries, counters: p.counters })}
            </p>
          )}
          {p.alreadyImported > 0 && (
            <p className="text-[12.5px] text-neutral-500">
              {t("import.previewAlready", { already: p.alreadyImported, new: p.new })}
            </p>
          )}
          {p.warnings.length > 0 && (
            <Fold label={t("import.notes", { n: p.warnings.length })}>
              <ul className="list-disc pl-5 text-[12.5px] text-neutral-500">
                {p.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Fold>
          )}
          <div className="flex gap-2">
            <Button disabled={busy || p.new === 0} onClick={() => void run()} data-testid="import-run">
              {t("import.run", { n: p.new })}
            </Button>
            <Button variant="secondary" onClick={() => setPreview(null)}>
              {t("import.cancel")}
            </Button>
          </div>
        </div>
      )}
      {message && (
        <StatusText tone={message.ok ? "ok" : "err"} testId="import-message">
          {message.text}
        </StatusText>
      )}
    </SettingsSection>
  );
}
